import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { database } from '../../config/backend';
import { ref, get as rtdbGet, push, set, child } from '../../services/phpDataAdapter';
import './Assessment.css';

interface LabExam {
  id?: string;
  date: string;
  type: string;
  results: Record<string, { value: string; unit?: string; reference?: string }>;
  observations?: string;
  createdAt?: string;
}

interface ExamsFormProps {
  patientId: string;
  onSave?: () => void;
}

const COMMON_EXAM_TYPES = [
  'Hemograma Completo',
  'Glicemia de Jejum',
  'Hemoglobina Glicada (HbA1c)',
  'Colesterol Total',
  'HDL',
  'LDL',
  'Triglicerídeos',
  'TSH',
  'T4 Livre',
  'Vitamina D',
  'Ferritina',
  'Ácido Úrico',
  'Creatinina',
  'Ureia',
  'TGO/AST',
  'TGP/ALT',
  'Outro',
];

const COMMON_PARAMETERS: Record<string, string[]> = {
  'Hemograma Completo': ['Hemácias', 'Hemoglobina', 'Hematócrito', 'Leucócitos', 'Plaquetas'],
  'Glicemia de Jejum': ['Glicemia'],
  'Hemoglobina Glicada (HbA1c)': ['HbA1c'],
  'Colesterol Total': ['Colesterol Total'],
  'HDL': ['HDL'],
  'LDL': ['LDL'],
  'Triglicerídeos': ['Triglicerídeos'],
  'TSH': ['TSH'],
  'T4 Livre': ['T4 Livre'],
  'Vitamina D': ['Vitamina D (25-OH)'],
  'Ferritina': ['Ferritina'],
  'Ácido Úrico': ['Ácido Úrico'],
  'Creatinina': ['Creatinina'],
  'Ureia': ['Ureia'],
  'TGO/AST': ['TGO/AST'],
  'TGP/ALT': ['TGP/ALT'],
};

export const ExamsForm: React.FC<ExamsFormProps> = ({ patientId, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exams, setExams] = useState<LabExam[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [currentExam, setCurrentExam] = useState<Partial<LabExam>>({
    date: new Date().toISOString().split('T')[0],
    type: '',
    results: {},
  });
  const [editingExamId, setEditingExamId] = useState<string | null>(null);
  const [currentParameter, setCurrentParameter] = useState({
    name: '',
    value: '',
    unit: '',
    reference: '',
  });

  useEffect(() => {
    if (patientId) {
      loadExams();
    }
  }, [patientId]);

  const loadExams = async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const snap = await rtdbGet(child(ref(database), `users/${patientId}/assessment/exams`));
      if (snap.exists()) {
        const examsData = snap.val() as Record<string, LabExam>;
        const examsList = Object.entries(examsData).map(([id, exam]) => ({
          id,
          ...exam,
        }));
        examsList.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setExams(examsList);
      } else {
        setExams([]);
      }
    } catch (error) {
      console.error('Erro ao carregar exames:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExam = () => {
    setCurrentExam({
      date: new Date().toISOString().split('T')[0],
      type: '',
      results: {},
    });
    setEditingExamId(null);
    setCurrentParameter({ name: '', value: '', unit: '', reference: '' });
    setShowAddDialog(true);
  };

  const handleEditExam = (exam: LabExam) => {
    setCurrentExam(exam);
    setEditingExamId(exam.id || null);
    setCurrentParameter({ name: '', value: '', unit: '', reference: '' });
    setShowAddDialog(true);
  };

  const handleAddParameter = () => {
    if (!currentParameter.name || !currentParameter.value) return;
    const results = { ...currentExam.results };
    results[currentParameter.name] = {
      value: currentParameter.value,
      unit: currentParameter.unit || undefined,
      reference: currentParameter.reference || undefined,
    };
    setCurrentExam({ ...currentExam, results });
    setCurrentParameter({ name: '', value: '', unit: '', reference: '' });
  };

  const handleRemoveParameter = (paramName: string) => {
    const results = { ...currentExam.results };
    delete results[paramName];
    setCurrentExam({ ...currentExam, results });
  };

  const handleSaveExam = async () => {
    if (!patientId || !currentExam.type || !currentExam.date) {
      alert('Preencha tipo e data do exame');
      return;
    }

    setSaving(true);
    try {
      const examData: LabExam = {
        date: currentExam.date,
        type: currentExam.type,
        results: currentExam.results || {},
        observations: currentExam.observations,
        createdAt: new Date().toISOString(),
      };

      if (editingExamId) {
        await set(ref(database, `users/${patientId}/assessment/exams/${editingExamId}`), examData);
      } else {
        const newExamRef = push(ref(database, `users/${patientId}/assessment/exams`));
        await set(newExamRef, examData);
      }

      await loadExams();
      setShowAddDialog(false);
      if (onSave) onSave();
      alert('Exame salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar exame:', error);
      alert('Erro ao salvar exame');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExam = async (examId: string) => {
    if (!window.confirm('Deseja realmente excluir este exame?')) return;
    try {
      await set(ref(database, `users/${patientId}/assessment/exams/${examId}`), null);
      await loadExams();
    } catch (error) {
      console.error('Erro ao excluir exame:', error);
      alert('Erro ao excluir exame');
    }
  };

  const getParametersForType = (type: string): string[] => {
    return COMMON_PARAMETERS[type] || [];
  };

  if (loading) {
    return (
      <Card title="Exames Laboratoriais" subtitle="Carregando...">
        <p>Carregando exames...</p>
      </Card>
    );
  }

  return (
    <>
      <Card
        title="Exames Laboratoriais"
        subtitle="Registre e acompanhe os exames laboratoriais do paciente"
        actions={
          <Button onClick={handleAddExam}>Novo Exame</Button>
        }
      >
        <div className="ns-exams-list">
          {exams.length === 0 ? (
            <p className="ns-empty-state">Nenhum exame registrado ainda.</p>
          ) : (
            exams.map((exam) => (
              <div key={exam.id} className="ns-exam-card">
                <div className="ns-exam-header">
                  <div>
                    <h4>{exam.type}</h4>
                    <p className="ns-exam-date">
                      {new Date(exam.date).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="ns-exam-actions">
                    <button
                      type="button"
                      className="ns-btn-edit"
                      onClick={() => handleEditExam(exam)}
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      className="ns-btn-delete"
                      onClick={() => exam.id && handleDeleteExam(exam.id)}
                    >
                      Excluir
                    </button>
                  </div>
                </div>

                {exam.observations && (
                  <p className="ns-exam-observations">{exam.observations}</p>
                )}

                <div className="ns-exam-results">
                  <table className="ns-table">
                    <thead>
                      <tr>
                        <th>Parâmetro</th>
                        <th>Valor</th>
                        <th>Unidade</th>
                        <th>Referência</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(exam.results || {}).map(([param, data]) => (
                        <tr key={param}>
                          <td>{param}</td>
                          <td><strong>{data.value}</strong></td>
                          <td>{data.unit || '-'}</td>
                          <td>{data.reference || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      <Modal
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        title={editingExamId ? 'Editar Exame' : 'Novo Exame'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAddDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveExam} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar Exame'}
            </Button>
          </>
        }
      >
        <div className="ns-exam-form">
          <label className="ns-field">
            <span>Tipo de Exame *</span>
            <select
              className="ns-input"
              value={currentExam.type}
              onChange={(e) => {
                setCurrentExam({ ...currentExam, type: e.target.value, results: {} });
                setCurrentParameter({ name: '', value: '', unit: '', reference: '' });
              }}
            >
              <option value="">Selecione o tipo</option>
              {COMMON_EXAM_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>

          <label className="ns-field">
            <span>Data do Exame *</span>
            <input
              type="date"
              className="ns-input"
              value={currentExam.date}
              onChange={(e) => setCurrentExam({ ...currentExam, date: e.target.value })}
            />
          </label>

          {currentExam.type && (
            <div className="ns-parameters-section">
              <h4>Parâmetros do Exame</h4>
              <div className="ns-parameter-form">
                <label className="ns-field">
                  <span>Nome do Parâmetro</span>
                  <select
                    className="ns-input"
                    value={currentParameter.name}
                    onChange={(e) => {
                      const paramName = e.target.value;
                      setCurrentParameter({ ...currentParameter, name: paramName });
                      // Preencher se já existe
                      if (currentExam.results?.[paramName]) {
                        const existing = currentExam.results[paramName];
                        setCurrentParameter({
                          name: paramName,
                          value: existing.value,
                          unit: existing.unit || '',
                          reference: existing.reference || '',
                        });
                      }
                    }}
                  >
                    <option value="">Selecione ou digite</option>
                    {getParametersForType(currentExam.type).map((param) => (
                      <option key={param} value={param}>
                        {param}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="ns-form-row">
                  <label className="ns-field">
                    <span>Valor *</span>
                    <input
                      type="text"
                      className="ns-input"
                      value={currentParameter.value}
                      onChange={(e) => setCurrentParameter({ ...currentParameter, value: e.target.value })}
                      placeholder="Ex: 95"
                    />
                  </label>

                  <label className="ns-field">
                    <span>Unidade</span>
                    <input
                      type="text"
                      className="ns-input"
                      value={currentParameter.unit}
                      onChange={(e) => setCurrentParameter({ ...currentParameter, unit: e.target.value })}
                      placeholder="Ex: mg/dL"
                    />
                  </label>
                </div>

                <label className="ns-field">
                  <span>Faixa de Referência</span>
                  <input
                    type="text"
                    className="ns-input"
                    value={currentParameter.reference}
                    onChange={(e) => setCurrentParameter({ ...currentParameter, reference: e.target.value })}
                    placeholder="Ex: 70-100 mg/dL"
                  />
                </label>

                <Button
                  variant="secondary"
                  onClick={currentParameter.name && currentExam.results?.[currentParameter.name] ? handleAddParameter : handleAddParameter}
                  disabled={!currentParameter.name || !currentParameter.value}
                >
                  {currentParameter.name && currentExam.results?.[currentParameter.name] ? 'Atualizar' : 'Adicionar'} Parâmetro
                </Button>
              </div>

              {Object.keys(currentExam.results || {}).length > 0 && (
                <div className="ns-parameters-list">
                  <h5>Parâmetros Adicionados:</h5>
                  <ul>
                    {Object.entries(currentExam.results || {}).map(([param, data]) => (
                      <li key={param}>
                        <strong>{param}:</strong> {data.value} {data.unit || ''}
                        {data.reference && ` (Ref: ${data.reference})`}
                        <button
                          type="button"
                          className="ns-btn-remove-small"
                          onClick={() => handleRemoveParameter(param)}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <label className="ns-field">
            <span>Observações</span>
            <textarea
              className="ns-textarea"
              rows={3}
              value={currentExam.observations || ''}
              onChange={(e) => setCurrentExam({ ...currentExam, observations: e.target.value })}
              placeholder="Observações sobre o exame..."
            />
          </label>
        </div>
      </Modal>
    </>
  );
};

